/**
 * The one place `console` is allowed. ESLint forbids it everywhere else.
 *
 * Namespaces match 04-architecture.md §8. In production only errors survive,
 * so debug logging can be left in place without shipping noise.
 */

export type LogNamespace = 'timeline' | 'db' | 'sync' | 'migration' | 'pwa' | 'app'

const isDev = import.meta.env.DEV

// Tests deliberately exercise failure paths, so their logs are noise that
// buries real failures. Silence everything under test unless VITE_LOG is set,
// which is how you get the output back when diagnosing one:
//
//     VITE_LOG=1 npm test
//
// Deliberately no reference to `process`: it does not exist in Safari, and a
// bare mention would throw a ReferenceError on the phone rather than in CI.
const isTest = import.meta.env.MODE === 'test'
const silent = isTest && !import.meta.env['VITE_LOG']

function prefix(ns: LogNamespace): string {
  return `[${ns}]`
}

export const logger = {
  debug(ns: LogNamespace, ...args: unknown[]): void {
    if (isDev && !silent) console.log(prefix(ns), ...args)
  },

  info(ns: LogNamespace, ...args: unknown[]): void {
    if (isDev && !silent) console.info(prefix(ns), ...args)
  },

  warn(ns: LogNamespace, ...args: unknown[]): void {
    if (!silent) console.warn(prefix(ns), ...args)
  },

  /** Always logged in dev and production; silenced only under test. */
  error(ns: LogNamespace, ...args: unknown[]): void {
    if (!silent) console.error(prefix(ns), ...args)
  },
}
